import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../slices/types';
import { 
  fetchApps, 
  createApp, 
  updateApp, 
  deleteApp,
  clearError, 
  clearSuccess,
  type App,
  type CreateAppData,
  type UpdateAppData
} from '../../slices/apps/appsSlice';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  RefreshCw,
  Smartphone,
  Calendar,
  Code,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';

const ManageApps: React.FC = () => {
  const dispatch = useAppDispatch();
  const { apps, loading, error, success, totalCount } = useAppSelector((state) => state.Apps);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [createForm, setCreateForm] = useState<CreateAppData>({
    app_name: '',
    version: '1.0.0'
  });
  const [editForm, setEditForm] = useState<UpdateAppData>({
    id: '',
    app_name: '',
    version: '',
    signup_bonus_enabled: false,
    signup_bonus_amount: '0.00'
  });

  // Fetch apps on component mount
  useEffect(() => {
    dispatch(fetchApps());
  }, [dispatch]);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [success, error, dispatch]);

  // Filter apps based on search term
  const filteredApps = apps.filter(app =>
    app.app_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle create app
  const handleCreateApp = () => {
    if (!createForm.app_name.trim()) {
      toast.error('App name is required');
      return;
    }
    dispatch(createApp(createForm));
    setCreateForm({ app_name: '', version: '1.0.0' });
    setShowCreateModal(false);
  };

  // Handle edit app
  const handleEditApp = (app: App) => {
    setSelectedApp(app);
    setEditForm({
      id: app.id,
      app_name: app.app_name,
      version: app.version,
      signup_bonus_enabled: app.signup_bonus_enabled ?? false,
      signup_bonus_amount: app.signup_bonus_amount ?? '0.00'
    });
    setShowEditModal(true);
  };

  // Handle update app
  const handleUpdateApp = async () => {
    if (!editForm.app_name?.trim()) {
      toast.error('App name is required');
      return;
    }

    const signupBonusAmount = Number(
      editForm.signup_bonus_amount ?? '0'
    );

    if (
      !Number.isFinite(signupBonusAmount) ||
      signupBonusAmount < 0
    ) {
      toast.error('Signup bonus amount must be zero or greater');
      return;
    }

    if (
      editForm.signup_bonus_enabled &&
      signupBonusAmount <= 0
    ) {
      toast.error(
        'Enter a signup bonus amount greater than zero before enabling it'
      );
      return;
    }

    const result = await dispatch(
      updateApp({
        ...editForm,
        signup_bonus_amount: signupBonusAmount.toFixed(2)
      })
    );

    if (updateApp.fulfilled.match(result)) {
      setShowEditModal(false);
      dispatch(fetchApps());
    }
  };

  // Handle delete app
  const handleDeleteApp = (id: string) => {
    if (window.confirm('Are you sure you want to delete this app?')) {
      dispatch(deleteApp(id));
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Smartphone className="w-8 h-8 mr-3 text-blue-600" />
              Manage Apps
            </h1>
            <p className="text-gray-600 mt-1">Create and manage your applications</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create App
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Smartphone className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Apps</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Apps</p>
              <p className="text-2xl font-bold text-gray-900">{apps.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Code className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Latest Version</p>
              <p className="text-2xl font-bold text-gray-900">1.0.0</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search apps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => dispatch(fetchApps())}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Apps Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading apps...</span>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="text-center py-12">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No apps found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first app'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Create App
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    App Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    App ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <Smartphone className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{app.app_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {app.version}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {app.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(app.updated_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditApp(app)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit App"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteApp(app.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete App"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create App Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Create New App</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App Name *
                </label>
                <input
                  type="text"
                  value={createForm.app_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, app_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter app name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version
                </label>
                <input
                  type="text"
                  value={createForm.version}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, version: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter version (e.g., 1.0.0)"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
              <div className="flex gap-3">
                <button
                  onClick={handleCreateApp}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Create App
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit App Modal */}
      {showEditModal && selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Edit App</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App Name *
                </label>
                <input
                  type="text"
                  value={editForm.app_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, app_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter app name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version
                </label>
                <input
                  type="text"
                  value={editForm.version || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, version: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter version (e.g., 1.0.0)"
                />
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      First-Time Signup Bonus
                    </h4>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      Credit this amount once after a genuine new user
                      successfully completes registration verification.
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={Boolean(editForm.signup_bonus_enabled)}
                    onClick={() =>
                      setEditForm(prev => ({
                        ...prev,
                        signup_bonus_enabled:
                          !prev.signup_bonus_enabled
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      editForm.signup_bonus_enabled
                        ? 'bg-green-600'
                        : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        editForm.signup_bonus_enabled
                          ? 'translate-x-5'
                          : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                <div className="mt-4">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Bonus Amount
                  </label>

                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm font-semibold text-gray-500">
                      ₹
                    </span>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      disabled={!editForm.signup_bonus_enabled}
                      value={editForm.signup_bonus_amount ?? '0.00'}
                      onChange={(e) =>
                        setEditForm(prev => ({
                          ...prev,
                          signup_bonus_amount: e.target.value
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                      placeholder="10.00"
                    />
                  </div>

                  <p className="mt-2 text-xs text-gray-500">
                    Change this later from ₹10 to ₹15 or any other amount
                    without modifying signup bonus code.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateApp}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Update App
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageApps;
